import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'

/**
 * Which ChangeQueue operation a non-group item of a batch commit persists as: `rotate` for a rotate
 * batch, `update` when the item touches size, rotation, fill, or an effect / wrap field (a full-blob
 * update), and `move` for a pure translation.
 */
export function resolveBatchItemPersistOperation(
  i: BatchShapeTransformItem,
  source: 'rotate' | 'move' | 'align' | 'resize' | 'update'
): 'rotate' | 'update' | 'move' {
  const touchesSizeOrRotation =
    i.after.rotation !== undefined ||
    i.before.rotation !== undefined ||
    i.after.width !== undefined ||
    i.before.width !== undefined ||
    i.after.height !== undefined ||
    i.before.height !== undefined
  const touchesEffects =
    i.after.roundedCorner !== undefined ||
    i.before.roundedCorner !== undefined ||
    i.after.opacity !== undefined ||
    i.before.opacity !== undefined ||
    i.after.sides !== undefined ||
    i.before.sides !== undefined ||
    i.after.inset !== undefined ||
    i.before.inset !== undefined ||
    i.after.wrapStyle !== undefined ||
    i.before.wrapStyle !== undefined ||
    i.after.wrapOffsetTop !== undefined ||
    i.before.wrapOffsetTop !== undefined ||
    i.after.wrapOffsetRight !== undefined ||
    i.before.wrapOffsetRight !== undefined ||
    i.after.wrapOffsetBottom !== undefined ||
    i.before.wrapOffsetBottom !== undefined ||
    i.after.wrapOffsetLeft !== undefined ||
    i.before.wrapOffsetLeft !== undefined
  // Key presence, not value: a None fill writes `fillColorId: undefined` and still has to reach the
  // full-element update, since `elements/move` keeps every field but x/y.
  const touchesPaint = 'fill' in i.after || 'fillColorId' in i.after || 'fillTint' in i.after

  return source === 'rotate'
    ? 'rotate'
    : touchesSizeOrRotation || touchesEffects || touchesPaint
      ? 'update'
      : 'move'
}
